import { Checkbox } from "@a2z/ui-v2";
import type {
  FieldSchema,
  ImageValue,
  LocaleString,
} from "@a2z/storefront-schemas";

/**
 * Renders one schema field from `@a2z/storefront-schemas`.
 *
 * Every control's SHAPE — its options, labels, ranges, defaults, `showIf`
 * gating — comes from the shared registry, so it cannot drift from what the
 * seller editor or the storefront believe. What is admin-local is only the
 * presentation.
 *
 * **The one deliberate presentational gap.** A `select` with `ui: "thumbnail"`
 * (the hero/about/faq variant pickers) renders here as labelled radio tiles,
 * NOT as the little layout sketches the seller editor draws. Those sketches are
 * `heroVariantThumbs.tsx` — 1534 lines that already exist in two hand-maintained
 * copies which drifted to 131 vs 93 glyphs, so every ported variant silently
 * advertised the wrong layout. A third copy is the single worst thing this file
 * could do. The option set and current value are identical; only the picture is
 * missing, and the live preview to the left shows the real thing. The
 * extraction that closes the gap is specified in
 * `docs/plans/2026-07-20-admin-theme-studio.md` §2.
 *
 * Fields intentionally NOT editable here (they render as read-only notes):
 * `image`, `productPicker`, `categoryPicker`. A catalog theme ships no imagery
 * — `applyThemeToStore` blanks every image field on apply and a preset test
 * asserts they are null — and it cannot name a specific store's product or
 * category, which is exactly why the `$category:N` / `$products:N` slot
 * vocabulary exists. Offering an upload button or a product picker here would
 * be a control that looks live and cannot affect any seller's store.
 */

type Lang = "ar" | "en";

interface FieldRendererProps {
  field: FieldSchema;
  value: unknown;
  lang: Lang;
  onChange: (value: unknown) => void;
}

function labelOf(
  label: { ar?: string; en?: string } | undefined,
  lang: Lang,
  fallback: string,
): string {
  return label?.[lang] ?? label?.en ?? label?.ar ?? fallback;
}

function asLocaleString(value: unknown): LocaleString {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    return {
      ar: typeof record.ar === "string" ? record.ar : null,
      en: typeof record.en === "string" ? record.en : null,
    };
  }
  // A flat string authored before the field became bilingual: surface it in
  // both slots rather than dropping it.
  if (typeof value === "string") return { ar: value, en: value };
  return { ar: null, en: null };
}

const inputClass =
  "w-full rounded-panel border border-border bg-card px-3 py-2 text-body text-text outline-none focus:border-brand";

export function FieldRenderer({
  field,
  value,
  lang,
  onChange,
}: FieldRendererProps) {
  const title = labelOf(field.label, lang, field.id);
  const help = field.helpText
    ? labelOf(field.helpText, lang, "")
    : "";

  const wrap = (control: React.ReactNode) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-secondary font-semibold text-text">
        {title}
      </label>
      {control}
      {help ? (
        <p className="text-label text-muted">{help}</p>
      ) : null}
    </div>
  );

  switch (field.type) {
    case "text":
    case "textarea":
    case "richText": {
      const multiline = field.type !== "text";
      if (field.i18n) {
        const localized = asLocaleString(value);
        return wrap(
          <div className="flex flex-col gap-2">
            {(["en", "ar"] as const).map((code) => {
              const commit = (next: string) =>
                onChange({ ...localized, [code]: next });
              return (
                <div key={code} className="flex items-start gap-2">
                  <span className="mt-2 w-6 shrink-0 text-label font-bold uppercase text-muted">
                    {code}
                  </span>
                  {multiline ? (
                    <textarea
                      dir={code === "ar" ? "rtl" : "ltr"}
                      rows={3}
                      className={inputClass}
                      value={localized[code] ?? ""}
                      onChange={(e) => commit(e.target.value)}
                    />
                  ) : (
                    <input
                      type="text"
                      dir={code === "ar" ? "rtl" : "ltr"}
                      className={inputClass}
                      value={localized[code] ?? ""}
                      onChange={(e) => commit(e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>,
        );
      }
      return wrap(
        multiline ? (
          <textarea
            rows={3}
            className={inputClass}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input
            type="text"
            className={inputClass}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
          />
        ),
      );
    }

    case "url":
    case "datetime":
      return wrap(
        <input
          type={field.type === "datetime" ? "datetime-local" : "text"}
          dir="ltr"
          className={inputClass}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />,
      );

    case "select": {
      const options = field.options.filter((o) => o.hidden !== true);
      const current =
        typeof value === "string"
          ? value
          : typeof field.default === "string"
            ? field.default
            : "";
      // `thumbnail` renders as labelled tiles rather than a dropdown so a
      // variant picker still reads as a set of choices you scan, which is what
      // it is — see the file header for why there is no sketch on the tile.
      if (field.ui === "thumbnail") {
        return wrap(
          <div className="grid grid-cols-2 gap-1.5">
            {options.map((option) => {
              const active = option.value === current;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onChange(option.value)}
                  className={`rounded-panel border px-2.5 py-2 text-start text-label font-medium transition ${
                    active
                      ? "border-brand bg-raised text-brand-hover"
                      : "border-border bg-card text-text hover:border-border2"
                  }`}
                >
                  {labelOf(option.label, lang, option.value)}
                </button>
              );
            })}
          </div>,
        );
      }
      return wrap(
        <select
          className={inputClass}
          value={current}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {labelOf(option.label, lang, option.value)}
            </option>
          ))}
        </select>,
      );
    }

    case "range": {
      const step = field.step ?? 1;
      const current =
        typeof value === "number"
          ? value
          : typeof field.default === "number"
            ? field.default
            : field.min;
      return wrap(
        <div className="flex items-center gap-3">
          <input
            type="range"
            className="flex-1 accent-brand"
            min={field.min}
            max={field.max}
            step={step}
            value={current}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          <span className="w-12 shrink-0 text-end text-secondary font-semibold tabular-nums text-text">
            {current}
          </span>
        </div>,
      );
    }

    case "checkbox": {
      const current =
        typeof value === "boolean"
          ? value
          : typeof field.default === "boolean"
            ? field.default
            : false;
      return (
        <label className="flex items-center gap-2.5">
          <Checkbox
            checked={current}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span className="text-secondary font-semibold text-text">
            {title}
          </span>
        </label>
      );
    }

    case "color": {
      const raw =
        typeof value === "string"
          ? value
          : typeof field.default === "string"
            ? field.default
            : "";
      const isSet = raw.trim().length > 0;
      // An UNSET colour must not render as a black swatch. Many of these fields
      // mean "inherit from the theme" when empty (their own help text says so),
      // and showing `#000000` claims the author picked black — a control that
      // looks correct and states the opposite of the truth. So: empty text box,
      // muted "inherit" swatch, and a Clear button to get back to unset once a
      // value has been picked (the native colour input has no empty state).
      return wrap(
        <div className="flex items-center gap-2">
          <div className="relative size-9 shrink-0">
            <input
              type="color"
              aria-label={title}
              className="size-9 cursor-pointer rounded-panel border border-border bg-transparent"
              value={/^#[0-9a-fA-F]{6}$/.test(raw) ? raw : "#ffffff"}
              onChange={(e) => onChange(e.target.value)}
            />
            {isSet ? null : (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-panel border border-dashed border-border bg-field text-label font-bold text-muted"
              >
                —
              </span>
            )}
          </div>
          <input
            type="text"
            dir="ltr"
            placeholder="inherit"
            className={inputClass}
            value={raw}
            onChange={(e) => onChange(e.target.value)}
          />
          {isSet ? (
            <button
              type="button"
              onClick={() => onChange("")}
              className="shrink-0 rounded-panel border border-border px-2 py-1.5 text-label font-semibold text-muted hover:text-text"
            >
              {"×"}
            </button>
          ) : null}
        </div>,
      );
    }

    case "colorScheme":
    case "headingLevel": {
      const options =
        field.type === "colorScheme"
          ? (["light", "dark", "auto"] as const)
          : ([1, 2, 3, 4] as const);
      const current = value ?? field.default;
      return wrap(
        <div className="flex gap-1.5">
          {options.map((option) => {
            const active = option === current;
            return (
              <button
                key={String(option)}
                type="button"
                onClick={() => onChange(option)}
                className={`flex-1 rounded-panel border px-2 py-1.5 text-label font-semibold transition ${
                  active
                    ? "border-brand bg-raised text-brand-hover"
                    : "border-border bg-card text-text"
                }`}
              >
                {field.type === "headingLevel" ? `H${option}` : option}
              </button>
            );
          })}
        </div>,
      );
    }

    case "image": {
      const image = (value ?? null) as ImageValue | null;
      // Read-only by design — see the file header. A theme ships no imagery, so
      // an upload control here would be a dead control by construction.
      return wrap(
        <div className="rounded-panel border border-dashed border-border px-3 py-2.5">
          <p className="text-label text-muted">
            {image?.url
              ? "Set on this theme — sellers get an empty slot on apply."
              : "Themes ship no imagery. Sellers fill this slot themselves."}
          </p>
        </div>,
      );
    }

    case "productPicker":
    case "categoryPicker":
      // Read-only by design — a catalog theme cannot name one store's product
      // or category. That is what the `$products:N` / `$category:N` slots are
      // for, and they are authored in the raw-JSON escape hatch.
      return wrap(
        <div className="rounded-panel border border-dashed border-border px-3 py-2.5">
          <p className="text-label text-muted">
            Bound per store on apply. Use the JSON editor for
            <code className="mx-1 font-mono">$category:N</code>/
            <code className="mx-1 font-mono">$products:N</code> slots.
          </p>
        </div>,
      );

    default: {
      // Exhaustiveness guard: a NEW field type added to the shared schema
      // surfaces here as a visible, named gap instead of an empty row that
      // reads as a working-but-blank control.
      const unknownField = field as FieldSchema;
      return wrap(
        <div className="rounded-panel border border-dashed border-warning px-3 py-2.5">
          <p className="text-label text-warning">
            Unsupported field type
            <code className="mx-1 font-mono">{unknownField.type}</code>— edit it
            in the JSON editor.
          </p>
        </div>,
      );
    }
  }
}
